import React, { useState, useEffect, useCallback } from "react";
import { Formik } from "formik";
import FilledInput from "@material-ui/core/FilledInput";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Iframe from "react-iframe";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { fetchMetabaseEmbedToken } from "./ReportsCrud";

const OPTIONAL_FILTERS = [
	{ key: "outbound_lpn", label: "Outbound LPN" },
	{ key: "product_name", label: "Product Name" },
	{ key: "item_number", label: "Item Number" },
	{ key: "category", label: "Category" },
];

const INITIAL_FILTER_VALUES = {
	outbound_lpn: "",
	product_name: "",
	item_number: "",
	category: "",
};

function MetabaseEmbedCard({ reportConfig, accessToken, isDashboard, path }) {
	const [embedUrl, setEmbedUrl] = useState("");
	const [iframeKey, setIframeKey] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const reportParameters = Array.isArray(reportConfig.parameters)
		? reportConfig.parameters
		: [];
	const visibleFilters = OPTIONAL_FILTERS.filter(filter =>
		reportParameters.includes(filter.key)
	);

	const loadEmbedToken = useCallback(
		async (filters = {}) => {
			if (!accessToken || !reportConfig) {
				return;
			}

			setLoading(true);
			setError(null);

			const body = {
				isDashboard: !!isDashboard,
				filters,
			};

			if (!isDashboard && path) {
				body.path = path;
			}

			try {
				const response = await fetchMetabaseEmbedToken(accessToken, body);
				setEmbedUrl(response.data.data.embedUrl);
				setIframeKey(prev => prev + 1);
			} catch (err) {
				const message =
					err.response && err.response.data && err.response.data.msg
						? err.response.data.msg
						: "There was an error loading this report. Please contact support.";
				setError(message);
				setEmbedUrl("");
			} finally {
				setLoading(false);
			}
		},
		[accessToken, reportConfig, isDashboard, path]
	);

	useEffect(() => {
		loadEmbedToken({});
	}, [loadEmbedToken]);

	const buildFiltersPayload = values => {
		const filters = {};
		visibleFilters.forEach(filter => {
			const value = values[filter.key];
			if (value && String(value).trim() !== "") {
				filters[filter.key] = String(value).trim();
			}
		});
		return filters;
	};

	return (
		<Card>
			<CardHeader title={reportConfig.name}>
				<CardHeaderToolbar />
			</CardHeader>
			<CardBody>
				{visibleFilters.length > 0 && (
					<Formik
						initialValues={INITIAL_FILTER_VALUES}
						onSubmit={values => loadEmbedToken(buildFiltersPayload(values))}
					>
						{({ values, handleSubmit, handleChange, handleBlur }) => (
							<form
								onSubmit={handleSubmit}
								className="form form-label-right mb-5"
							>
								<div className="form-group row">
									{visibleFilters.map(filter => (
										<div className="col-lg-3" key={filter.key}>
											<FormControl
												variant="filled"
												style={{ width: "100%" }}
											>
												<InputLabel htmlFor={filter.key}>
													{filter.label}
												</InputLabel>
												<FilledInput
													id={filter.key}
													name={filter.key}
													value={values[filter.key]}
													onChange={handleChange}
													onBlur={handleBlur}
													className="form-control form-control-solid h-auto py-2 px-6"
												/>
											</FormControl>
										</div>
									))}
									<div className="col-lg-3 d-flex align-items-end">
										<button
											type="submit"
											className="btn btn-primary"
											disabled={loading}
										>
											Apply
										</button>
									</div>
								</div>
							</form>
						)}
					</Formik>
				)}

				{loading && (
					<div className="text-center py-10">Loading report...</div>
				)}

				{error && !loading && (
					<div className="alert alert-danger" role="alert">
						{error}
					</div>
				)}

				{!loading && !error && embedUrl && (
					<div className="col-12">
						<Iframe
							key={iframeKey}
							url={embedUrl}
							width="100%"
							height="800px"
							id="metabase-embed"
							className="metabase-embed"
							display="initial"
							position="relative"
						/>
					</div>
				)}
			</CardBody>
		</Card>
	);
}

export default MetabaseEmbedCard;
